import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime, formatRelative } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import {
  createApiKey,
  createWebhook,
  deleteWebhook,
  listApiKeys,
  listDeliveries,
  listWebhooks,
  patchWebhook,
  retryDelivery,
  revokeApiKey,
  type ApiKeyRow,
  type DeliveryRow,
  type WebhookRow,
} from './api';

/**
 * Integrations (context.md §121, §123): API keys and webhooks.
 *
 * Two things the screen never does: show a credential twice, and pretend a failing receiver is
 * fine. A key or a secret appears once, in a box made for copying; a subscription's deliveries are
 * listed with what came back, and a disabled one says why.
 */
export function IntegrationsPage(): React.JSX.Element {
  return (
    <div className="view">
      <PageHeader title={t('integrations.title')} description={t('integrations.description')} />
      <ApiKeysPanel />
      <WebhooksPanel />
      <Panel title={t('integrations.docs')}>
        <p className="muted">
          {t('integrations.docsHint')}{' '}
          <a href={`${apiBase()}/api/openapi.json`} target="_blank" rel="noreferrer">
            openapi.json
          </a>
        </p>
        <pre className="code-sample">{`curl -H "Authorization: Bearer sk_live_…" ${apiBase()}/v1/crm/leads`}</pre>
      </Panel>
    </div>
  );
}

function apiBase(): string {
  return (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3380';
}

/** A credential shown once. */
function Reveal({
  label,
  value,
  onDone,
}: {
  label: string;
  value: string;
  onDone: () => void;
}): React.JSX.Element {
  const [copied, setCopied] = useState(false);

  return (
    <div className="reveal">
      <p className="strong">{label}</p>
      <p className="muted small">{t('integrations.revealHint')}</p>
      <code className="token">{value}</code>
      <div className="cell-inline">
        <button
          type="button"
          className="button button--compact"
          onClick={() => {
            void navigator.clipboard?.writeText(value).then(() => setCopied(true));
          }}
        >
          {copied ? t('integrations.copied') : t('integrations.copy')}
        </button>
        <button type="button" className="button button--quiet button--compact" onClick={onDone}>
          {t('integrations.savedIt')}
        </button>
      </div>
    </div>
  );
}

function ApiKeysPanel(): React.JSX.Element {
  const client = useQueryClient();
  const keys = useQuery({ queryKey: ['api-keys'], queryFn: listApiKeys });
  const [name, setName] = useState('');
  const [readOnly, setReadOnly] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = (): void => void client.invalidateQueries({ queryKey: ['api-keys'] });

  const create = useMutation({
    mutationFn: () => createApiKey({ name: name.trim(), scopes: readOnly ? READ_SCOPES : null }),
    onSuccess: (created) => {
      setRevealed(created.key);
      setName('');
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const revoke = useMutation({
    mutationFn: revokeApiKey,
    onSuccess: refresh,
    onError: (c: unknown) => setError(messageFor(c)),
  });

  const keyColumns: Column<ApiKeyRow>[] = [
    {
      key: 'name',
      header: t('integrations.keyName'),
      render: (key) => <span className="strong">{key.name}</span>,
    },
    {
      key: 'prefix',
      header: t('integrations.keyPrefix'),
      render: (key) => <code>{key.prefix}…</code>,
    },
    {
      key: 'scopes',
      header: t('integrations.scopes'),
      text: (key) =>
        key.scopes
          ? t('integrations.scopesN').replace('{n}', String(key.scopes.length))
          : t('integrations.fullAccess'),
      render: (key) => (
        <span className="muted small">
          {key.scopes
            ? t('integrations.scopesN').replace('{n}', String(key.scopes.length))
            : t('integrations.fullAccess')}
        </span>
      ),
    },
    {
      key: 'lastUsedAt',
      header: t('integrations.lastUsed'),
      text: (key) =>
        key.lastUsedAt ? formatRelative(key.lastUsedAt) : t('integrations.neverUsed'),
      render: (key) => (
        <span className="muted small">
          {key.lastUsedAt ? formatRelative(key.lastUsedAt) : t('integrations.neverUsed')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      text: () => '',
      render: (key) =>
        key.revokedAt ? (
          <Badge tone="neutral">{t('integrations.revoked')}</Badge>
        ) : (
          <button
            type="button"
            className="button button--quiet button--danger button--compact"
            disabled={revoke.isPending}
            onClick={() => revoke.mutate(key.id)}
          >
            {t('integrations.revoke')}
          </button>
        ),
    },
    // Off by default: who made the key and when it stops working — asked once, not daily.
    {
      key: 'createdAt',
      header: t('common.createdAt'),
      hidden: true,
      render: (key) => formatDateTime(key.createdAt),
    },
    {
      key: 'expiresAt',
      header: t('integrations.expiresAt'),
      hidden: true,
      render: (key) => (key.expiresAt ? formatDateTime(key.expiresAt) : '—'),
    },
  ];

  return (
    <Panel title={t('integrations.apiKeys')}>
      <p className="muted small">{t('integrations.apiKeysHint')}</p>

      <DataTable
        tableKey="api-keys"
        rows={keys.data?.keys}
        columns={keyColumns}
        rowKey={(key) => key.id}
        isLoading={keys.isLoading}
        error={keys.error}
        onRetry={() => void keys.refetch()}
        empty={{ title: t('integrations.noKeys') }}
      />

      {revealed ? (
        <Reveal
          label={t('integrations.yourKey')}
          value={revealed}
          onDone={() => setRevealed(null)}
        />
      ) : (
        <form
          className="cell-inline"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <input
            value={name}
            maxLength={120}
            placeholder={t('integrations.keyNamePlaceholder')}
            onChange={(event) => setName(event.target.value)}
          />
          <label className="cell-inline small">
            <input
              type="checkbox"
              checked={readOnly}
              onChange={(event) => setReadOnly(event.target.checked)}
            />
            {t('integrations.readOnly')}
          </label>
          <button
            type="submit"
            className="button button--primary button--compact"
            disabled={name.trim() === '' || create.isPending}
          >
            {t('integrations.createKey')}
          </button>
        </form>
      )}

      {error && (
        <p className="alert small" role="alert">
          {error}
        </p>
      )}
    </Panel>
  );
}

/** The read side of the product, for a key that only reads. */
const READ_SCOPES = [
  'leads:read',
  'contacts:read',
  'companies:read',
  'opportunities:read',
  'tasks:read',
  'projects:read',
  'inbox:read',
  'forms:read',
  'pages:read',
  'calendar:read',
  'files:read',
  'support:read',
  'cs:read',
  'proposals:read',
  'social:read',
  'ads:read',
  'analytics:read',
  'integrations:read',
];

const EVENT_CHOICES = [
  'crm.lead.*',
  'crm.contact.*',
  'crm.company.*',
  'crm.opportunity.*',
  'sales.proposal.*',
  'message.inbound.received',
  'message.conversation.*',
  'form.submission.received',
  'work.task.*',
  'calendar.booking.*',
  'support.ticket.*',
  'cs.account.*',
  '*',
];

function deliveryTone(status: DeliveryRow['status']): BadgeTone {
  return status === 'DELIVERED'
    ? 'positive'
    : status === 'DEAD'
      ? 'danger'
      : status === 'FAILED'
        ? 'warning'
        : 'info';
}

function WebhooksPanel(): React.JSX.Element {
  const client = useQueryClient();
  const hooks = useQuery({ queryKey: ['webhooks'], queryFn: listWebhooks });
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>(['crm.lead.*']);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = (): void => void client.invalidateQueries({ queryKey: ['webhooks'] });

  const create = useMutation({
    mutationFn: () => createWebhook({ name: name.trim(), url: url.trim(), events }),
    onSuccess: (created) => {
      setRevealed(created.secret);
      setName('');
      setUrl('');
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const toggle = useMutation({
    mutationFn: (hook: WebhookRow) => patchWebhook(hook.id, { active: !hook.active }),
    onSuccess: refresh,
    onError: (c: unknown) => setError(messageFor(c)),
  });

  const remove = useMutation({
    mutationFn: deleteWebhook,
    onSuccess: refresh,
    onError: (c: unknown) => setError(messageFor(c)),
  });

  return (
    <Panel title={t('integrations.webhooks')}>
      <p className="muted small">{t('integrations.webhooksHint')}</p>

      {hooks.isLoading ? (
        <Loading />
      ) : hooks.error ? (
        <ErrorState error={hooks.error} onRetry={() => void hooks.refetch()} />
      ) : (
        <>
          {(hooks.data?.subscriptions ?? []).map((hook) => (
            <div key={hook.id} className="hook">
              <div className="hook__row">
                <span className="cell-stack">
                  <span className="strong">{hook.name}</span>
                  <span className="muted small">{hook.url}</span>
                  <span className="muted small">{hook.events.join(' · ')}</span>
                  {hook.disabledAt && (
                    <span className="small" style={{ color: 'var(--color-down)' }}>
                      {hook.disabledReason}
                    </span>
                  )}
                </span>
                <span className="cell-inline">
                  <Badge tone={hook.disabledAt ? 'danger' : hook.active ? 'positive' : 'neutral'}>
                    {hook.disabledAt
                      ? t('integrations.disabled')
                      : hook.active
                        ? t('integrations.active')
                        : t('integrations.paused')}
                  </Badge>
                  <button
                    type="button"
                    className="button button--quiet button--compact"
                    onClick={() => setOpen(open === hook.id ? null : hook.id)}
                  >
                    {t('integrations.deliveries')}
                  </button>
                  <button
                    type="button"
                    className="button button--quiet button--compact"
                    disabled={toggle.isPending}
                    onClick={() => toggle.mutate(hook)}
                  >
                    {hook.active ? t('integrations.pause') : t('integrations.resume')}
                  </button>
                  <button
                    type="button"
                    className="button button--quiet button--danger button--compact"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(hook.id)}
                  >
                    {t('social.delete')}
                  </button>
                </span>
              </div>
              {open === hook.id && <Deliveries id={hook.id} />}
            </div>
          ))}

          {revealed ? (
            <Reveal
              label={t('integrations.yourSecret')}
              value={revealed}
              onDone={() => setRevealed(null)}
            />
          ) : (
            <form
              className="stack"
              onSubmit={(event) => {
                event.preventDefault();
                if (name.trim() && url.trim() && events.length > 0) create.mutate();
              }}
            >
              <div className="form-grid">
                <label className="stack__field">
                  <span>{t('integrations.hookName')}</span>
                  <input
                    value={name}
                    maxLength={120}
                    onChange={(event) => setName(event.target.value)}
                  />
                </label>
                <label className="stack__field">
                  <span>{t('integrations.hookUrl')}</span>
                  <input
                    type="url"
                    value={url}
                    placeholder="https://"
                    onChange={(event) => setUrl(event.target.value)}
                  />
                </label>
              </div>
              <div className="stack__field">
                <span>{t('integrations.events')}</span>
                <div className="chips">
                  {EVENT_CHOICES.map((choice) => (
                    <button
                      key={choice}
                      type="button"
                      className={`chip${events.includes(choice) ? ' chip--active' : ''}`}
                      onClick={() =>
                        setEvents(
                          events.includes(choice)
                            ? events.filter((e) => e !== choice)
                            : [...events, choice],
                        )
                      }
                    >
                      {choice}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <button
                  type="submit"
                  className="button button--primary button--compact"
                  disabled={!name.trim() || !url.trim() || events.length === 0 || create.isPending}
                >
                  {t('integrations.createHook')}
                </button>
              </div>
            </form>
          )}
        </>
      )}

      {error && (
        <p className="alert small" role="alert">
          {error}
        </p>
      )}
    </Panel>
  );
}

function Deliveries({ id }: { id: string }): React.JSX.Element {
  const client = useQueryClient();
  const deliveries = useQuery({ queryKey: ['deliveries', id], queryFn: () => listDeliveries(id) });
  const retry = useMutation({
    mutationFn: retryDelivery,
    onSuccess: () => void client.invalidateQueries({ queryKey: ['deliveries', id] }),
  });

  const columns: Column<DeliveryRow>[] = [
    {
      key: 'eventType',
      header: t('integrations.event'),
      render: (row) => <code className="small">{row.eventType}</code>,
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      render: (row) => <Badge tone={deliveryTone(row.status)}>{row.status}</Badge>,
    },
    {
      key: 'attempts',
      header: t('integrations.attempts'),
      align: 'end',
      text: (row) => String(row.attempts),
      render: (row) => row.attempts,
    },
    {
      key: 'response',
      header: t('integrations.response'),
      text: (row) => `${row.responseStatus ?? ''} ${row.lastError ?? ''}`,
      render: (row) => (
        <span className="muted small">
          {row.responseStatus ?? '—'}
          {row.lastError && ` · ${row.lastError.slice(0, 60)}`}
        </span>
      ),
    },
    {
      key: 'when',
      header: t('integrations.when'),
      text: (row) => formatDateTime(row.deliveredAt ?? row.createdAt),
      render: (row) => (
        <span className="muted small">{formatDateTime(row.deliveredAt ?? row.createdAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      text: () => '',
      render: (row) =>
        row.status !== 'DELIVERED' && row.status !== 'SENDING' ? (
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={retry.isPending}
            onClick={() => retry.mutate(row.id)}
          >
            {t('integrations.retry')}
          </button>
        ) : null,
    },
    // Off by default: the ids somebody needs only when chasing one delivery through the logs.
    {
      key: 'eventId',
      header: t('integrations.eventId'),
      hidden: true,
      render: (row) => <code className="small">{row.eventId.slice(0, 8)}</code>,
    },
    {
      key: 'nextAttemptAt',
      header: t('integrations.nextAttempt'),
      hidden: true,
      render: (row) => formatDateTime(row.nextAttemptAt),
    },
  ];

  return (
    <DataTable
      tableKey="webhook-deliveries"
      rows={deliveries.data?.deliveries}
      columns={columns}
      rowKey={(row) => row.id}
      isLoading={deliveries.isLoading}
      error={deliveries.error}
      onRetry={() => void deliveries.refetch()}
      empty={{ title: t('integrations.noDeliveries') }}
    />
  );
}
